import { Dispatch, SetStateAction, useState } from "react";
import dayjs from "dayjs";
import classNames from "classnames";
import { Button, Typography } from "components/atoms";
import { BackIcon, Spinner } from "components/atoms/Icon";
import { useGetOverallProgressQuery } from "services/homeApi";
import { FetchError } from "components/molecules";
import { GuaranteeEligibilityGridMobile } from "../GuaranteeEligibilityGridMobile";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import { matchTestLink } from "utils/functions/matchTestLink";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import { useAppSelector } from "hooks";
import "./index.scss";

interface IProps {
  setIsShowMobileViewAllSummary?: Dispatch<SetStateAction<boolean>>;
  theme?: "light" | "dark";
}

export const ViewAllSummaryMobile = ({
  setIsShowMobileViewAllSummary,
  theme,
}: IProps) => {
  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;
  const testType = payload ? payload.testtype : null;

  const { data, isLoading, isError, isFetching, refetch } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [viewDetails, setViewDetails] = useState(false);

  const { aiMCATChatDrawerStatus } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status } }) =>
      state.layout
  );

  const scoreData = [
    ...(data && data.ShowDiagnostics
      ? [
          {
            courseItem: "Diagnostic Exams",
            complete:
              data &&
              "DiagnosticsCompletedCount" in data &&
              data.DiagnosticsCompletedCount,
            incomplete:
              data &&
              "DiagnosticsIncompleteCount" in data &&
              data.DiagnosticsIncompleteCount,
            upcoming:
              data &&
              "DiagnosticsUpcomingCount" in data &&
              data.DiagnosticsUpcomingCount,
          },
        ]
      : []),
    ...(data && data.ShowAttendance
      ? [
          {
            courseItem: "Attendance",
            complete:
              data &&
              "AttendanceCompletedCount" in data &&
              data.AttendanceCompletedCount,
            incomplete:
              data &&
              "AttendanceIncompleteCount" in data &&
              data.AttendanceIncompleteCount,
            upcoming:
              data &&
              "AttendanceUpcomingCount" in data &&
              data.AttendanceUpcomingCount,
          },
        ]
      : []),
    ...(data && data.ShowHomework
      ? [
          {
            courseItem: "Homework",
            complete:
              data &&
              "HomeworkCompletedCount" in data &&
              data.HomeworkCompletedCount,
            incomplete:
              data &&
              "HomeworkIncompleteCount" in data &&
              data.HomeworkIncompleteCount,
            upcoming:
              data &&
              "HomeworkUpcomingCount" in data &&
              data.HomeworkUpcomingCount,
          },
        ]
      : []),
    ...(data && data.ShowTests
      ? [
          {
            courseItem: "Test",
            complete:
              data && "TestsCompletedCount" in data && data.TestsCompletedCount,
            incomplete:
              data &&
              "TestsIncompleteCount" in data &&
              data.TestsIncompleteCount,
            upcoming:
              data && "TestsUpcomingCount" in data && data.TestsUpcomingCount,
          },
        ]
      : []),
    ...(data && data.ShowTopicFocus
      ? [
          {
            courseItem: getOfficeHoursDisplayName(testType || ""),
            complete:
              data &&
              "TopicFocusCompletedCount" in data &&
              data.TopicFocusCompletedCount,
            incomplete:
              data &&
              "TopicFocusIncompleteCount" in data &&
              data.TopicFocusIncompleteCount,
            upcoming:
              data &&
              "TopicFocusUpcomingCount" in data &&
              data.TopicFocusUpcomingCount,
          },
        ]
      : []),
  ];

  const handleBack = () => {
    setIsShowMobileViewAllSummary &&
      setIsShowMobileViewAllSummary((prev) => !prev);
  };

  const handleClick = () => {
    const url = matchTestLink(testType);
    window.open(url, "__blank");
  };

  const isRisky = (data: any) => {
    if (
      data &&
      data.DiagnosticsIncompleteCount === 0 &&
      data.AttendanceIncompleteCount === 0 &&
      data.HomeworkIncompleteCount === 0 &&
      data.TestsIncompleteCount === 0 &&
      data.TopicFocusIncompleteCount === 0
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className={classNames("view-all-mobile-wrapper")}>
      <div className={classNames("view-all-mobile-back-wrapper")}>
        <BackIcon color={"#233851"} onClickHandle={handleBack} />
        <Typography variant="h6Medium" label="Back" onClick={handleBack} />
      </div>
      <div className={classNames("view-all-mobile-title-wrapper")}>
        <Typography variant="h6Bold" label="Progress Summary Overview" />
      </div>
      <div className={classNames("view-all-summary-score-wrapper")}>
        <div className={classNames("title-wrapper")}>
          <div className={classNames("baseline-score-wrapper")}>
            <Typography variant="h7Book" label="Baseline Score:" />
            {( data.QualifyingScoreStatus === null || data.QualifyingScoreStatus === undefined ) ? (
              <Typography label="N/A" variant="h7Med" />
            ) : (
              <Typography
                variant="h7Med"
                label={data.QualifyingScoreStatus}
                styles="score-text"
              />
            )}
          </div>
          <div className={classNames("target-date-wrapper")}>
            <Typography variant="h7Book" label="Target Test Date:" />
            {( data.TargetTestDate === null || data.TargetTestDate === undefined ) ? (
              <Typography label="N/A" variant="h7Med" />
            ) : (
              <Typography
                variant="h7Med"
                label={dayjs(data.TargetTestDate.TestDate).format(
                  "MMMM D, YYYY"
                )}
              />
            )}
          </div>
        </div>
        {isLoading ? (
          <Spinner theme={theme} />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : (
          <>
            {scoreData.map((value: any, index: number) => (
              <div
                key={index}
                className={classNames({
                  "course-item-wrapper": true,
                  highlight: index % 2 === 0,
                })}
              >
                <div className={classNames("item-title-wrapper")}>
                  <Typography variant="h8Med" label="Course Item" />
                  <Typography variant="h8Book" label={value.courseItem} />
                </div>
                <div className={classNames("item-scores-wrapper")}>
                  <div className={classNames("score-with-tag-wrapper")}>
                    <Typography variant="h8Med" label="Complete" />
                    <div className={classNames("tag complete")}>
                      <Typography variant="h9Book" label={value.complete} />
                    </div>
                  </div>
                  <div className={classNames("score-with-tag-wrapper")}>
                    <Typography variant="h8Med" label="Incomplete" />
                    <div className={classNames("tag incomplete")}>
                      <Typography variant="h9Book" label={value.incomplete} />
                    </div>
                  </div>
                  <div className={classNames("score-with-tag-wrapper")}>
                    <Typography variant="h8Med" label="Upcoming" />
                    <div className={classNames("tag upcoming")}>
                      <Typography variant="h9Book" label={value.upcoming} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div
              className={classNames(
                "eligibility-section-wrapper",
                aiMCATChatDrawerStatus === "closed"
                  ? "eligibility-section-wrapper-ai-chat-drawer-closed"
                  : ""
              )}
            >
              <div className="title-sction">
                <div className="eligibilty-score-wrapper">
                  <Typography variant="h7Bold" label="Guarantee Eligibility:" />
                  <Typography
                    variant="h7Med"
                    label={isRisky(data) ? "At Risk" : "No Risk"}
                    styles={isRisky(data) ? "score-text" : "success-text"}
                  />
                </div>
                {isRisky(data) ? (
                  <Button
                    label="View Details"
                    type="planner"
                    icon={false}
                    onClick={() => setViewDetails((prev) => !prev)}
                    styles="view-detail-btn-custom-style"
                  />
                ) : null}
              </div>
              {isRisky(data) && viewDetails ? (
                <GuaranteeEligibilityGridMobile
                  overallProgressData={data}
                  theme={theme}
                  isFetching={isFetching}
                  onRefresh={refetch}
                />
              ) : null}
              <Typography
                variant="h7Med"
                label="Want to learn more about our Guarantee Requirements?"
              />
              <Button
                label="Click Here"
                type="planner"
                icon={false}
                styles="view-detail-btn-custom-style"
                onClick={handleClick}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
